import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { makeStyles } from '@material-ui/core/styles';
import { Toolbar } from '@devexpress/dx-react-grid-material-ui';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';

import {
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  Grow,
  IconButton,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip
} from '@mui/material';

import RefreshIcon from '@material-ui/icons/Refresh';
import UploadFileIcon from '@mui/icons-material/UploadFile';

export const BULK_ACTIONS_BY_STATUS = {
  RequiresReview: [
    { value: 'approve', label: 'Approve' },
    { value: 'delete', label: 'Delete' }
  ],
  Approved: [
    { value: 'submit', label: 'Submit' },
    { value: 'delete', label: 'Delete' }
  ]
};

const useStyles = makeStyles((theme) => ({
  toolbar: {
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    padding: theme.spacing(1),
    '& > div:first-of-type': {
      display: 'none'
    }
  }
}));

const InvoiceBulkActionGroup = ({ actions, selectedAction, onActionChange, onAction, getActionCount, bulkAction }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);
  const selectedOption = actions.find(({ value }) => value === selectedAction) || actions[0];
  const count = getActionCount(selectedOption.value);

  const closeMenu = (event) => {
    if (anchorRef.current?.contains(event.target)) return;
    setOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup ref={anchorRef} variant="contained" size="small" aria-label="Invoice bulk actions">
        <Button disabled={!count || Boolean(bulkAction)} onClick={() => onAction(selectedOption.value)}>
          {bulkAction === selectedOption.value ? (
            <React.Fragment>
              <CircularProgress size={16} color="inherit" className="tw-mr-2" />
              {selectedOption.label}…
            </React.Fragment>
          ) : (
            `${selectedOption.label} (${count})`
          )}
        </Button>
        <Button
          size="small"
          aria-controls={open ? 'invoice-bulk-action-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          aria-label="Choose invoice bulk action"
          disabled={Boolean(bulkAction)}
          onClick={() => setOpen((current) => !current)}
          className="tw-z-50"
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper open={open} anchorEl={anchorRef.current} placement="bottom-end" transition style={{ zIndex: 1400 }}>
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={closeMenu}>
                <MenuList id="invoice-bulk-action-menu" autoFocusItem>
                  {actions
                    .filter((action) => action.value !== selectedOption.value)
                    .map((action) => (
                      <MenuItem
                        key={action.value}
                        onClick={() => {
                          onActionChange(action.value);
                          setOpen(false);
                        }}
                      >
                        {action.label}
                      </MenuItem>
                    ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};

const CustomToolbarRoot = ({
  children,
  total,
  onRefresh,
  loading,
  activeStatus,
  selectedCount,
  selectedTotalLabel,
  invalidInvoiceCount,
  selectedBulkAction,
  onBulkActionChange,
  onBulkAction,
  getBulkActionCount,
  bulkAction,
  onImport,
  ...props
}) => {
  const classes = useStyles();
  const actions = BULK_ACTIONS_BY_STATUS[activeStatus] || [];
  return (
    <Toolbar.Root {...props} className={classes.toolbar}>
      <strong>{total} bills</strong>
      {children}
      <Button variant="contained" size="small" startIcon={<UploadFileIcon />} onClick={onImport}>
        Import
      </Button>
      {selectedCount > 0 && (
        <strong>
          {selectedCount} selected · Total {selectedTotalLabel}
        </strong>
      )}
      {invalidInvoiceCount > 0 && (
        <strong style={{ color: '#d32f2f' }}>
          {invalidInvoiceCount} {invalidInvoiceCount === 1 ? 'bill needs' : 'bills need'} price review
        </strong>
      )}
      <Tooltip title="Refresh bills">
        <span>
          <IconButton onClick={onRefresh} disabled={loading} size="small">
            <RefreshIcon className={loading ? 'tw-animate-spin' : ''} />
          </IconButton>
        </span>
      </Tooltip>
      {actions.length > 0 && (
        <span style={{ display: 'inline-flex' }}>
          <InvoiceBulkActionGroup
            actions={actions}
            selectedAction={selectedBulkAction}
            onActionChange={onBulkActionChange}
            onAction={onBulkAction}
            getActionCount={getBulkActionCount}
            bulkAction={bulkAction}
          />
        </span>
      )}
    </Toolbar.Root>
  );
};

export default CustomToolbarRoot;

CustomToolbarRoot.propTypes = {
  children: PropTypes.any,
  total: PropTypes.any,
  onRefresh: PropTypes.any,
  loading: PropTypes.any,
  activeStatus: PropTypes.any,
  selectedCount: PropTypes.any,
  selectedTotalLabel: PropTypes.string,
  invalidInvoiceCount: PropTypes.number,
  selectedBulkAction: PropTypes.any,
  onBulkActionChange: PropTypes.any,
  onBulkAction: PropTypes.any,
  getBulkActionCount: PropTypes.any,
  bulkAction: PropTypes.any,
  onImport: PropTypes.func
};

InvoiceBulkActionGroup.propTypes = {
  actions: PropTypes.any,
  selectedAction: PropTypes.any,
  onActionChange: PropTypes.any,
  onAction: PropTypes.any,
  getActionCount: PropTypes.any,
  bulkAction: PropTypes.any
};
